import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const RoyalPromiseSection = ({ onWhatsAppClick }) => {
  const [isVisible, setIsVisible] = useState(true); // Changed to true for mobile compatibility
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 } // Lowered threshold for better mobile detection
    );

    if (sectionRef?.current) {
      observer?.observe(sectionRef?.current);
    }

    return () => observer?.disconnect();
  }, []);

  const promises = [
    {
      icon: "Zap",
      title: "Instant Payouts",
      description: "Lightning-fast withdrawals processed within 5 minutes, 24/7. Your winnings reach your account faster than anywhere else.",
      commitment: "Under 5 minutes guaranteed"
    },
    {
      icon: "MessageCircle",
      title: "24/7 WhatsApp Support",
      description: "Direct access to gaming experts through WhatsApp. Get personalized assistance, game recommendations, and instant problem resolution.",
      commitment: "Response within 30 seconds"
    },
    {
      icon: "Shield",
      title: "Responsible Gaming",
      description: "Comprehensive tools and support for safe gaming. Set limits, take breaks, and access professional help whenever needed.",
      commitment: "Your wellbeing first"
    },
    {
      icon: "Star",
      title: "Fair Play Guarantee",
      description: "Certified random number generators and transparent game mechanics. Every game is fair, every outcome is genuine.",
      commitment: "100% fair and transparent"
    },
    {
      icon: "Lock",
      title: "Data Protection",
      description: "Bank-grade security for all your personal and financial information. Your privacy and security are our top priorities.",
      commitment: "Military-grade encryption"
    },
    {
      icon: "Crown",
      title: "VIP Treatment",
      description: "Every player receives royal treatment regardless of stake size. Personalized service, exclusive offers, and premium experiences.",
      commitment: "Royal treatment for all"
    }
  ];

  const values = [
    {
      title: "Integrity",
      description: "We operate with complete transparency and honesty in all our dealings.",
      icon: "Heart"
    },
    {
      title: "Excellence",
      description: "We strive for perfection in every aspect of our service and platform.",
      icon: "Award"
    },
    {
      title: "Innovation",
      description: "We continuously evolve to provide cutting-edge gaming experiences.",
      icon: "Lightbulb"
    },
    {
      title: "Community",
      description: "We build lasting relationships and foster a supportive gaming community.",
      icon: "Users"
    }
  ];

  return (
    <section ref={sectionRef} className="py-20 bg-gradient-to-br from-gray-900 via-gray-800 to-black relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-20 left-10 w-40 h-40 bg-primary/5 rounded-full animate-float"></div>
        <div className="absolute bottom-20 right-10 w-32 h-32 bg-accent/5 rounded-full animate-float" style={{ animationDelay: '2s' }}></div>
      </div>
      <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className={`text-center mb-16 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 bg-gradient-royal rounded-full flex items-center justify-center shadow-royal animate-glow">
              <Icon name="Crown" size={32} color="white" />
            </div>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-white mb-6">
            Our <span className="text-gradient-royal">Royal Promise</span>
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            These aren't just commitments—they're the pillars of our royal dynasty. Every promise is backed by our reputation and delivered with unwavering dedication.
          </p>
        </div>

        {/* Promises Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-20">
          {promises?.map((promise, index) => (
            <div
              key={index}
              className={`transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
              style={{ transitionDelay: `${index * 200}ms` }}
            >
              <div className="bg-white/5 backdrop-blur-md rounded-2xl p-6 border border-white/10 hover:bg-white/10 hover:border-primary/30 transition-all duration-300 hover-lift group h-full">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-gradient-royal rounded-full flex items-center justify-center mr-4 group-hover:animate-pulse-golden">
                    <Icon name={promise?.icon} size={24} color="white" />
                  </div>
                  <h3 className="text-xl font-heading font-bold text-white">{promise?.title}</h3>
                </div>
                
                <p className="text-gray-300 mb-4 leading-relaxed">{promise?.description}</p>
                
                <div className="mt-auto">
                  <div className="bg-primary/20 rounded-lg p-3 border border-primary/30">
                    <p className="text-primary font-bold text-sm">{promise?.commitment}</p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Core Values */}
        <div className={`mb-16 transition-all duration-1000 delay-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <h3 className="text-2xl font-heading font-bold text-center text-white mb-12">
            Our <span className="text-gradient-royal">Core Values</span>
          </h3>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values?.map((value, index) => (
              <div
                key={index}
                className={`transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                style={{ transitionDelay: `${(index + 6) * 150}ms` }}
              >
                <div className="bg-white/5 backdrop-blur-md rounded-xl p-6 border border-white/10 hover:bg-white/10 transition-all duration-300 text-center">
                  <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon name={value?.icon} size={24} color="var(--color-primary)" />
                  </div>
                  <h4 className="text-lg font-bold text-white mb-2">{value?.title}</h4>
                  <p className="text-gray-400 text-sm">{value?.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <div className={`text-center transition-all duration-1000 delay-1200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="bg-gradient-to-r from-primary/20 to-accent/20 rounded-2xl p-8 border border-primary/30">
            <h3 className="text-2xl font-heading font-bold text-white mb-4">
              Experience the Royal Difference
            </h3>
            <p className="text-gray-300 mb-8 max-w-2xl mx-auto">
              Join thousands of players who trust NITIN for their gaming journey. Our royal promise isn't just words—it's our commitment to your success and satisfaction.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                variant="default"
                size="lg"
                onClick={onWhatsAppClick}
                iconName="MessageCircle"
                iconPosition="left"
                className="bg-gradient-royal hover:shadow-glow animate-pulse-golden hover:animate-none"
              >
                Speak to Our Gaming Expert
              </Button>
              <Button
                variant="outline"
                size="lg"
                className="border-white text-white hover:bg-white hover:text-gray-900"
              >
                View Our Credentials
              </Button>
            </div>

            {/* Trust Indicators */}
            <div className="flex flex-wrap justify-center gap-8 mt-8 pt-8 border-t border-white/20">
              <div className="flex items-center space-x-2">
                <Icon name="Shield" size={20} color="var(--color-success)" />
                <span className="text-sm text-gray-400">Licensed & Regulated</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="Clock" size={20} color="var(--color-primary)" />
                <span className="text-sm text-gray-400">5+ Years Trusted</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="Users" size={20} color="var(--color-accent)" />
                <span className="text-sm text-gray-400">50K+ Happy Players</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="Star" size={20} color="var(--color-warning)" />
                <span className="text-sm text-gray-400">4.9/5 Rating</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default RoyalPromiseSection;